import { apiRequest } from '@/lib/api';
/**
 * useRecommendations Hook
 * 
 * Custom React hook for fetching and managing entity recommendations
 * from the Emma recommendation engine.
 * 
 * Features:
 * - Async recommendation fetching with loading/error states
 * - Result caching to prevent redundant API calls
 * - Configurable entity type and limit
 * - Manual refetch capability
 * - Automatic error handling and recovery
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface RecommendationResponse {
  entity_id: string;
  name: string;
  type: string;
  reason: string;
  relevance_score: number;
  metadata: Record<string, any>;
}

interface UseRecommendationsParams {
  entityId: string;
  entityType: 'client' | 'partner' | 'solution';
  limit?: number;
  excludeIds?: string[];
  enabled?: boolean;
}

interface UseRecommendationsReturn {
  recommendations: RecommendationResponse[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isRefetching: boolean;
}

// Simple in-memory cache for recommendations
const recommendationCache = new Map<string, { data: RecommendationResponse[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cache key from request parameters
 */
const getCacheKey = (
  entityId: string,
  entityType: string,
  limit: number,
  excludeIds: string[]
): string => {
  const excludeStr = excludeIds.sort().join(',');
  return `${entityId}:${entityType}:${limit}:${excludeStr}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_TTL_MS;
};

/**
 * useRecommendations Hook
 * 
 * @example
 * const { recommendations, loading, error, refetch } = useRecommendations({
 *   entityId: 'partner-123',
 *   entityType: 'partner',
 *   limit: 5
 * });
 */
export const useRecommendations = ({
  entityId,
  entityType,
  limit = 5,
  excludeIds = [],
  enabled = true,
}: UseRecommendationsParams): UseRecommendationsReturn => {
  const [recommendations, setRecommendations] = useState<RecommendationResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  
  // Track mounted state to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  /**
   * Fetch recommendations from API
   */
  const fetchRecommendations = useCallback(async (isManualRefetch = false) => {
    if (!enabled) {
      return;
    }

    const cacheKey = getCacheKey(entityId, entityType, limit, excludeIds);
    
    // Check cache for non-manual fetches
    if (!isManualRefetch) {
      const cached = recommendationCache.get(cacheKey);
      if (cached && isCacheValid(cached.timestamp)) {
        if (isMountedRef.current) {
          setRecommendations(cached.data);
          setLoading(false);
          setError(null);
        }
        return;
      }
    }

    // Set loading state
    if (isMountedRef.current) {
      if (isManualRefetch) {
        setIsRefetching(true);
      } else {
        setLoading(true);
      }
      setError(null);
    }

    try {
      // Build request payload
      const payload = {
        entity_id: entityId,
        entity_type: entityType.toLowerCase(),
        limit: Math.max(1, Math.min(20, limit)), // Validate limit between 1-20
        ...(excludeIds && excludeIds.length > 0 && { exclude_ids: excludeIds }),
      };

      // Make API request
      const response = await apiRequest('/api/v1/emma/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Handle non-200 responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail ||
          `API returned ${response.status}: ${response.statusText}`;
        
        if (isMountedRef.current) {
          setError(errorMessage);
          setRecommendations(null);
        }
        return;
      }

      // Parse response
      const data = await response.json();

      // Validate response structure
      if (!data.recommendations || !Array.isArray(data.recommendations)) {
        if (isMountedRef.current) {
          setError('Invalid response format from recommendations API');
          setRecommendations(null);
        }
        return;
      }

      // Cache successful results
      recommendationCache.set(cacheKey, {
        data: data.recommendations,
        timestamp: Date.now(),
      });

      // Update state
      if (isMountedRef.current) {
        setRecommendations(data.recommendations);
        setError(null);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch recommendations';
      
      if (isMountedRef.current) {
        setError(errorMessage);
        setRecommendations(null);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsRefetching(false);
      }
    }
  }, [entityId, entityType, limit, excludeIds, enabled]);

  /**
   * Manual refetch function
   */
  const refetch = useCallback(async () => {
    await fetchRecommendations(true);
  }, [fetchRecommendations]);

  /**
   * Fetch on mount or when parameters change
   */
  useEffect(() => {
    isMountedRef.current = true;
    
    if (enabled && entityId) {
      fetchRecommendations(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [entityId, entityType, limit, excludeIds, enabled, fetchRecommendations]);

  return {
    recommendations,
    loading,
    error,
    refetch,
    isRefetching,
  };
};

/**
 * Utility function to clear the recommendations cache
 * Useful for testing or when data should be refreshed
 */
export const clearRecommendationsCache = (): void => {
  recommendationCache.clear();
};

/**
 * Utility function to prefetch recommendations
 * Can be called before component mount to warm the cache
 */
export const prefetchRecommendations = async (
  entityId: string,
  entityType: 'client' | 'partner' | 'solution',
  limit: number = 5
): Promise<void> => {
  const cacheKey = getCacheKey(entityId, entityType, limit, []);
  
  // Return if already cached and valid
  const cached = recommendationCache.get(cacheKey);
  if (cached && isCacheValid(cached.timestamp)) {
    return;
  }

  try {
    const response = await apiRequest('/api/v1/emma/recommendations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_id: entityId,
        entity_type: entityType.toLowerCase(),
        limit,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.recommendations && Array.isArray(data.recommendations)) {
        recommendationCache.set(cacheKey, {
          data: data.recommendations,
          timestamp: Date.now(),
        });
      }
    }
  } catch (err) {
    // Silently fail on prefetch - not critical
    console.debug('Recommendation prefetch failed:', err);
  }
};

export default useRecommendations;
